import { useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/components/ui/tabs";
import { Lock, Tags, Boxes } from "lucide-react";
import { ClassificationTaxonomyTab } from "../components/taxonomy/classification-taxonomy-tab";
import { ObjectPresenceTaxonomyTab } from "../components/taxonomy/object-presence-taxonomy-tab";

/* ──────────────────────────────────────────────────────────────────
 * TaxonomySettings — read-only reference for the ML taxonomies, one
 * tab per taxonomy. Renders the same taxonomy queries the Review UI
 * uses; no mutations of any kind.
 * ────────────────────────────────────────────────────────────────── */

const TaxonomySettings = () => {
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState("classification");

  const breadcrumbs = [
    { path: "/settings", breadcrumb: t("breadcrumbs.settings") },
    { path: "/settings/taxonomy", breadcrumb: t("breadcrumbs.taxonomy") },
  ];

  return (
    <div className="h-full">
      <PageHeader breadcrumbs={breadcrumbs} />
      <div className="p-6 space-y-6 max-w-5xl">
        {/* ─── Page header ─────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">{t("taxonomy.title")}</h1>
            <p className="text-muted-foreground">{t("taxonomy.description")}</p>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 text-xs font-medium text-amber-600 dark:text-amber-400">
            <Lock className="h-3 w-3" />
            {t("taxonomy.readOnly")}
          </span>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger
              value="classification"
              className="flex items-center gap-2"
            >
              <Tags className="h-4 w-4" />
              {t("taxonomy.tabClassification")}
            </TabsTrigger>
            <TabsTrigger
              value="object-presence"
              className="flex items-center gap-2"
            >
              <Boxes className="h-4 w-4" />
              {t("taxonomy.tabObjectPresence")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="classification">
            <ClassificationTaxonomyTab />
          </TabsContent>

          <TabsContent value="object-presence">
            <ObjectPresenceTaxonomyTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TaxonomySettings;
